/**
 * Documents in the dataset's index that no workspace source owns (#891): indexed with the
 * CLI indexer before the workspace existed. They are searchable, but their files were never
 * stored, so they can't be converted or re-indexed here. Folded until opened.
 *
 * Each shows the kind the tagger gave it and why. A dataset manager can change the kind
 * (fact / policy / opinion / none), which rewrites the document's chunk headers, and take a
 * document that is not a real source out of search (#1382).
 */
import React, { useEffect, useState } from 'react';
import { Terminal } from 'lucide-react';
import { Button, Actions, Select } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { listIndexedDocuments, updateIndexedDocument } from './datasetApi';
import { Section, SectionTitle, Lead, Meta, Table, TableScroll, KindChip, ErrorText } from './styles';

// A person sets these, or none; testimony is set only by the anonymising pipeline
const CLI_KINDS = ['fact', 'policy', 'opinion'];

function KindReason({ doc, t }) {
  if (doc.kind_from === 'person') return <Meta>{t('knowledgeKinds.cliDocs.byPerson')}</Meta>;
  return doc.kind_reason ? <Meta>{doc.kind_reason}</Meta> : null;
}

export default function IndexedDocuments({ datasetId, canManage = false }) {
  const { t } = useTranslation();
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    listIndexedDocuments(datasetId).then(setDocs).catch((err) => setError(err.message));
  }, [datasetId]);

  const save = async (doc, patch) => {
    setSaving(doc.id);
    setError(null);
    try {
      const updated = await updateIndexedDocument(datasetId, doc.id, patch);
      setDocs((list) => list.map((d) => (d.id === doc.id ? { ...d, ...updated } : d)));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  if (!docs) return error ? <Section><ErrorText>{error}</ErrorText></Section> : null;
  if (!docs.length) return null;
  const chunks = docs.reduce((sum, d) => sum + (d.chunk_count || 0), 0);

  return (
    <Section>
      <SectionTitle><Terminal size={18} /> {t('datasetWorkspace.cliDocs.title', { count: docs.length })}</SectionTitle>
      <Lead>{t('datasetWorkspace.cliDocs.lead', { chunks })}</Lead>
      {canManage && <Lead>{t('knowledgeKinds.cliDocs.manageLead')}</Lead>}
      {error && <ErrorText>{error}</ErrorText>}
      {!open ? (
        <Actions><Button $size="small" onClick={() => setOpen(true)}>{t('datasetWorkspace.cliDocs.show')}</Button></Actions>
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th>{t('datasetWorkspace.sourceName')}</th>
                <th>{t('datasetWorkspace.kind')}</th>
                <th>{t('datasetWorkspace.publisher')}</th>
                <th>{t('datasetWorkspace.link')}</th>
                <th>{t('datasetWorkspace.chunks')}</th>
                {canManage && <th>{t('knowledgeKinds.cliDocs.search')}</th>}
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} style={d.is_excluded ? { opacity: 0.5 } : undefined}>
                  <td>
                    {d.title || d.filename}
                    {d.is_excluded && <> ({t('datasetWorkspace.cliDocs.excluded')})</>}
                  </td>
                  <td>
                    {canManage && d.source_kind !== 'testimony' ? (
                      <Select value={d.source_kind || ''} disabled={saving === d.id}
                        aria-label={t('knowledgeKinds.cliDocs.kindOf', { title: d.title || d.filename })}
                        onChange={(e) => save(d, { source_kind: e.target.value || null })}>
                        <option value="">{t('datasetWorkspace.kinds.none')}</option>
                        {CLI_KINDS.map((k) => <option key={k} value={k}>{t(`datasetWorkspace.kinds.${k}`)}</option>)}
                      </Select>
                    ) : (
                      d.source_kind ? <KindChip $kind={d.source_kind}>{t(`datasetWorkspace.kinds.${d.source_kind}`)}</KindChip> : '–'
                    )}
                    <KindReason doc={d} t={t} />
                  </td>
                  <td>{d.author || '–'}</td>
                  <td>
                    {/^https?:\/\//i.test(d.source || '')
                      ? <a href={d.source} target="_blank" rel="noreferrer">{d.source}</a>
                      : '–'}
                  </td>
                  <td>{d.chunk_count}</td>
                  {canManage && (
                    <td>
                      <Button type="button" $size="small" disabled={saving === d.id}
                        onClick={() => save(d, { is_excluded: !d.is_excluded })}>
                        {t(d.is_excluded ? 'knowledgeKinds.cliDocs.include' : 'knowledgeKinds.cliDocs.exclude')}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}
    </Section>
  );
}
